import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  Subject,
  debounceTime,
  distinctUntilChanged,
  filter,
  takeUntil,
} from 'rxjs';
import * as ibantools from 'ibantools';
import { CompanySearchBankAccountItem } from 'src/app/shared/company-search-service/company-search.service';
import {
  ClientsService,
  SelectedClient,
} from 'src/app/services/clients/clients.service';
import { HelpersService } from 'src/app/services/helpers/helpers.service';
import { PayeeFormFieldComponent } from '../../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeInputDirective } from '../../../../shared/payee-input/payee-input.directive';
import { MatError } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { PayeeIconButtonComponent } from '../../../../shared/payee-buttons/payee-icon-button/payee-icon-button.component';
import { PayeeIconComponent } from '../../../../shared/payee-icon/payee-icon.component';
import { PayeeCompanySearchBankAccountPickerComponent } from 'src/app/shared/payee-company-search-bank-account-picker/payee-company-search-bank-account-picker.component';

@Component({
  selector: 'app-bank-account-edit',
  templateUrl: './bank-account-edit.component.html',
  styleUrls: ['./bank-account-edit.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    PayeeFormFieldComponent,
    PayeeInputDirective,
    MatError,
    TranslateModule,
    PayeeIconButtonComponent,
    PayeeIconComponent,
    PayeeCompanySearchBankAccountPickerComponent,
  ],
})
export class BankAccountEditComponent implements OnInit, OnDestroy {
  private clientsService = inject(ClientsService);
  private fb = inject(FormBuilder);
  private helperService = inject(HelpersService);
  private readonly destroy = new Subject<void>();

  readonly labelMaxLength = 32;

  readonly bankAccountForm = this.fb.group({
    label: this.fb.control('', {
      nonNullable: true,
      validators: Validators.maxLength(32),
    }),
    accountHolderName: this.fb.control('', { nonNullable: true }),
    accountNumber: this.fb.control('', {
      validators: this.helperService.bankAccountValidator,
      nonNullable: true,
    }),
    bankName: this.fb.control('', { nonNullable: true }),
  });

  readonly formatBankAccount = this.helperService.formatBankAccount;
  readonly bankAccountFormatExample = 'HU39162001511856250100000000';

  selectedClient: SelectedClient | null = null;

  saveCount = 0;

  ngOnInit(): void {
    this.clientsService.selectedClientSubject
      .pipe(
        takeUntil(this.destroy),
        filter((client): client is SelectedClient => !!client),
        distinctUntilChanged((previous, current) => previous.id === current.id),
      )
      .subscribe((client) => {
        this.selectedClient = client;
        this.loadBankAccountFromClient(client);
      });

    this.bankAccountForm.valueChanges
      .pipe(
        takeUntil(this.destroy),
        debounceTime(777),
        distinctUntilChanged(),
      )
      .subscribe(() => {
        void this.saveBankAccount();
      });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  focusLabelInput(input: HTMLInputElement): void {
    input.focus();
    input.select();
  }

  applyCompanySearchAccount(account: CompanySearchBankAccountItem): void {
    const accountNumber = account.iban || account.account_number || '';
    const bankName = account.bank_name ?? '';
    const current = this.bankAccountForm.getRawValue();
    if (
      current.accountHolderName === account.account_holder_name
      && current.bankName === bankName
      && this.accountNumbersMatch(current.accountNumber, accountNumber)
    ) {
      return;
    }

    this.bankAccountForm.patchValue({
      accountHolderName: account.account_holder_name,
      bankName,
      accountNumber,
    });
    this.bankAccountForm.markAsDirty();
    this.bankAccountForm.updateValueAndValidity({ emitEvent: true });
  }

  private accountNumbersMatch(
    left: string | null | undefined,
    right: string | null | undefined,
  ): boolean {
    const normalize = (value: string | null | undefined): string => {
      if (!value) {
        return '';
      }
      return ibantools.electronicFormatIBAN(value) || value.replace(/\s/g, '');
    };

    return normalize(left) === normalize(right);
  }

  private loadBankAccountFromClient(client: SelectedClient): void {
    const bankAccount = client.bankAccount;
    if (!bankAccount) {
      this.bankAccountForm.reset(undefined, { emitEvent: false });
      return;
    }

    this.bankAccountForm.patchValue(
      {
        label: bankAccount.label ?? '',
        accountHolderName: bankAccount.account_holder_name ?? '',
        accountNumber: bankAccount.iban || bankAccount.account_number || '',
        bankName: bankAccount.bank_name ?? '',
      },
      { emitEvent: false },
    );
    this.bankAccountForm.markAsPristine();
  }

  private async saveBankAccount(): Promise<void> {
    if (this.bankAccountForm.invalid || !this.bankAccountForm.dirty) {
      return;
    }

    try {
      const { label, accountHolderName, accountNumber, bankName } =
        this.bankAccountForm.getRawValue();
      const electronicFormatted = ibantools.electronicFormatIBAN(accountNumber);
      if (electronicFormatted == null) {
        return;
      }
      const isIban = ibantools.isValidIBAN(electronicFormatted);
      const isBban = ibantools.isValidBBAN(electronicFormatted, 'HU');

      await this.clientsService.updateBankAccount({
        label: label.trim() || null,
        bank_name: bankName || undefined,
        account_holder_name: accountHolderName || undefined,
        account_number: isBban ? electronicFormatted : undefined,
        iban: isIban ? electronicFormatted : undefined,
      });

      this.bankAccountForm.markAsPristine();
      this.showSaveIndicator();
    } catch (error) {
      console.error('Error while saving client bank account', error);
    }
  }

  private showSaveIndicator(): void {
    ++this.saveCount;
    setTimeout(() => --this.saveCount, 2500);
  }
}
